/**
 * <s-cursor> — CharlyCorp custom cursor
 * A lime dot that tracks the pointer exactly and a ring that trails it with easing.
 * The ring grows over interactive elements. Only activates for fine pointers
 * (mouse/trackpad) when the visitor has not asked for reduced motion; otherwise
 * the native cursor stays untouched.
 */
const INTERACTIVE = 'a, button, label, summary, select, [role="button"], input[type="submit"], input[type="checkbox"], input[type="radio"]';

class SCursor extends HTMLElement {
  #ac;
  #raf = 0;
  #x = -100;
  #y = -100;
  #ringX = -100;
  #ringY = -100;
  #dot;
  #ring;

  connectedCallback() {
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!finePointer.matches || reducedMotion.matches) return;

    this.#dot = this.querySelector('[data-cursor-dot]');
    this.#ring = this.querySelector('[data-cursor-ring]');
    if (!this.#dot || !this.#ring) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    // Deactivate live if the visitor switches to reduced motion mid-session.
    reducedMotion.addEventListener('change', () => this.#deactivate(), { signal });

    document.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.#x = event.clientX;
      this.#y = event.clientY;
      if (!this.hasAttribute('data-active')) {
        this.#ringX = this.#x;
        this.#ringY = this.#y;
        this.setAttribute('data-active', '');
      }
      this.removeAttribute('data-hidden');
    }, { signal, passive: true });

    document.addEventListener('pointerover', (event) => {
      const target = event.target instanceof Element ? event.target.closest(INTERACTIVE) : null;
      this.toggleAttribute('data-hover', Boolean(target));
    }, { signal, passive: true });

    document.addEventListener('pointerdown', () => this.setAttribute('data-press', ''), { signal, passive: true });
    document.addEventListener('pointerup', () => this.removeAttribute('data-press'), { signal, passive: true });
    document.documentElement.addEventListener('pointerleave', () => this.setAttribute('data-hidden', ''), { signal, passive: true });

    const tick = () => {
      this.#ringX += (this.#x - this.#ringX) * 0.2;
      this.#ringY += (this.#y - this.#ringY) * 0.2;
      this.#dot.style.translate = `${this.#x}px ${this.#y}px`;
      this.#ring.style.translate = `${this.#ringX}px ${this.#ringY}px`;
      this.#raf = requestAnimationFrame(tick);
    };
    this.#raf = requestAnimationFrame(tick);
  }

  disconnectedCallback() {
    this.#deactivate();
  }

  #deactivate() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
    this.removeAttribute('data-active');
  }
}

if (!customElements.get('s-cursor')) customElements.define('s-cursor', SCursor);
